import { useState, useEffect, useRef, startTransition } from "react";
import { useLocation } from "react-router";
import { useCosmosClient } from "@/services/cosmos";
import { refetchCheckoutAfterFailed3dsReport } from "@/lib/refetch-after-failed-3ds-report";
import type { CheckoutSession } from "@/types";

interface Use3dsFailureReportingArgs {
  shouldShowFailureLoading: boolean;
  mutateSession: (
    data?: CheckoutSession | Promise<CheckoutSession>,
    opts?: { revalidate?: boolean },
  ) => Promise<CheckoutSession | undefined>;
  onSessionFetched: (session: CheckoutSession) => void;
}

export function use3dsFailureReporting({
  shouldShowFailureLoading,
  mutateSession,
  onSessionFetched,
}: Use3dsFailureReportingArgs): boolean {
  const location = useLocation();
  const client = useCosmosClient();
  const hasReported3dsFailureRef = useRef(false);
  const [isReporting3dsFailure, setIsReporting3dsFailure] = useState(
    shouldShowFailureLoading,
  );

  useEffect(() => {
    const checkoutId = client.config().checkoutId;
    const storageKey = `reported_3ds_failure_${checkoutId}`;

    if (hasReported3dsFailureRef.current) {
      return;
    }

    if (shouldShowFailureLoading) {
      try {
        if (sessionStorage.getItem(storageKey) === "true") {
          startTransition(() => {
            setIsReporting3dsFailure(false);
          });
          return;
        }
      } catch {
        // sessionStorage blocked
      }

      hasReported3dsFailureRef.current = true;

      try {
        sessionStorage.setItem(storageKey, "true");
      } catch {
        // sessionStorage blocked
      }

      client
        .report3dsFailure()
        .then((sessionData) => {
          mutateSession(sessionData, { revalidate: false });
          onSessionFetched(sessionData);
          startTransition(() => {
            setIsReporting3dsFailure(false);
          });
        })
        .catch(async () => {
          await refetchCheckoutAfterFailed3dsReport(
            () => client.getCheckoutSessionV3(),
            mutateSession,
            onSessionFetched,
          );
          setTimeout(() => {
            startTransition(() => {
              setIsReporting3dsFailure(false);
            });
          }, 2000);
        });
    }
  }, [
    client,
    shouldShowFailureLoading,
    mutateSession,
    onSessionFetched,
    location.pathname,
    location.search,
  ]);

  return isReporting3dsFailure;
}
